export class WordPressClient {
  constructor(site) {
    if (!site) {
      throw new Error('WordPress site configuration is required');
    }
    this.site = site;
    this.credentials = null;
  }

  setCredentials(username, password) {
    this.credentials = { username, password };
  }

  getAuthHeader() {
    if (!this.credentials) {
      throw new Error('WordPress credentials not set');
    }
    return `Basic ${btoa(`${this.credentials.username}:${this.credentials.password}`)}`;
  }

  async uploadMedia(blob, filename) {
    try {
      const response = await fetch(`${this.site.api_url}/media`, {
        method: 'POST',
        headers: {
          'Authorization': this.getAuthHeader(),
          'Content-Disposition': `attachment; filename="${filename}"`,
          'Content-Type': blob.type,
        },
        body: blob
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Media upload failed: ${errorData.message || response.statusText}`);
      }

      const data = await response.json();
      return {
        id: data.id,
        url: data.source_url
      };
    } catch (error) {
      console.error('Media upload error:', error);
      throw error;
    }
  }

  async getMediaUrl(mediaId) {
    try {
      const response = await fetch(`${this.site.api_url}/media/${mediaId}`, {
        headers: {
          'Authorization': this.getAuthHeader()
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch media');
      }

      const data = await response.json();
      return data.source_url;
    } catch (error) {
      console.error('Error fetching media URL:', error);
      return null;
    }
  }


  async uploadFeaturedImage(imageUrl, title) {
      try {
        console.log('Attempting to download image from:', imageUrl);
        
        if (!imageUrl) {
          throw new Error('No image URL provided');
        }
        
        const response = await fetch(imageUrl);
        if (!response.ok) {
          throw new Error(`Failed to download image: ${response.statusText}`);
        }
    
        const blob = await response.blob();
        if (!blob || blob.size === 0) {
          throw new Error('Downloaded image is empty');
        }
    
        const filename = `${title}-${Date.now()}.${blob.type.split('/')[1]}`;
        console.log('Uploading image with filename:', filename);
        
        const uploadResult = await this.uploadMedia(blob, filename);
        console.log('Upload result:', uploadResult);
        
        if (!uploadResult?.id) {
          throw new Error('Media upload succeeded but no ID was returned');
        }
    
        return uploadResult.id;
      } catch (error) {
        console.error('Featured image upload error:', error);
        throw new Error(`Failed to upload featured image: ${error.message}`);
      }
  }

  async getUsers() {
    try {
      const response = await fetch(`${this.site.api_url}/users`, {
        headers: {
          'Authorization': this.getAuthHeader()
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Failed to fetch users: ${errorData.message || response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  

  async createPost({ 
    title, 
    content, 
    categoryId, 
    featuredMediaId, 
    authorId, 
    status = 'draft',
    // optional parameters
    excerpt,
    meta,
    slug,
    tags,
    commentStatus,
    pingStatus,
    format
  }) {
    try {
      const postData = {
        title: { raw: title },
        content: { raw: content },
        status,
        categories: categoryId ? [parseInt(categoryId)] : undefined,
        featured_media: featuredMediaId || 0,
        author: authorId ? parseInt(authorId) : undefined,
        // Only add these fields if they are provided
        ...(excerpt && { excerpt: { raw: excerpt } }),
        ...(meta && { meta }),
        ...(slug && { slug }),
        ...(tags && { tags }),
        ...(commentStatus && { comment_status: commentStatus }),
        ...(pingStatus && { ping_status: pingStatus }),
        ...(format && { format })
      };
  
      const response = await fetch(`${this.site.api_url}/posts`, {
        method: 'POST',
        headers: {
          'Authorization': this.getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(postData)
      });
  
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Post creation failed: ${errorData.message || response.statusText}`);
      }
  
      return await response.json();
    } catch (error) {
      console.error('Post creation error:', error);
      throw error;
    }
  }

  async getPosts({ 
    page = 1, 
    perPage = 10, 
    status = 'publish',
    search = '',
    category = null,
    orderBy = 'date',
    order = 'desc'
  } = {}) {
    try {
      let url = `${this.site.api_url}/posts?page=${page}&per_page=${perPage}&status=${status}`;
      
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      if (category) {
        url += `&categories=${category}`;
      }
      url += `&orderby=${orderBy}&order=${order}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': this.getAuthHeader()
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch posts');
      }

      const totalPages = parseInt(response.headers.get('X-WP-TotalPages') || '1');
      const totalPosts = parseInt(response.headers.get('X-WP-Total') || '0');
      const posts = await response.json();

      return {
        posts,
        totalPages,
        totalPosts
      };
    } catch (error) {
      console.error('Error fetching posts:', error);
      throw error;
    }
  }

  async getPost(postId) {
    try {
      const response = await fetch(`${this.site.api_url}/posts/${postId}`, {
        headers: {
          'Authorization': this.getAuthHeader()
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Failed to fetch post: ${errorData.message || response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching post:', error);
      throw error;
    }
  }

  async updatePost(postId, { 
    title, 
    content, 
    status,
    categoryId,
    featuredMediaId,
    excerpt,
    meta,
    slug,
    tags,
    commentStatus,
    pingStatus,
    format
  }) {
    try {
      const postData = {
        ...(title && { title: { raw: title } }),
        ...(content && { content: { raw: content } }),
        ...(status && { status }),
        ...(categoryId && { categories: [parseInt(categoryId)] }),
        ...(featuredMediaId && { featured_media: parseInt(featuredMediaId) }),
        ...(excerpt && { excerpt: { raw: excerpt } }),
        ...(meta && { meta }),
        ...(slug && { slug }),
        ...(tags && { tags }),
        ...(commentStatus && { comment_status: commentStatus }),
        ...(pingStatus && { ping_status: pingStatus }),
        ...(format && { format })
      };

      const response = await fetch(`${this.site.api_url}/posts/${postId}`, {
        method: 'POST',
        headers: {
          'Authorization': this.getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(postData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Post update failed: ${errorData.message || response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Post update error:', error);
      throw error;
    }
  }

  /**
   * Creates a tag or retrieves an existing one with the same name
   * @param {string} name - The tag name to create or retrieve
   * @returns {Promise<Object>} - The created/retrieved tag object with id
   */
  async createTag(name) {
    try {
      // First, try to find if the tag already exists
      const searchResponse = await fetch(`${this.site.api_url}/tags?search=${encodeURIComponent(name)}&per_page=1`, {
        headers: {
          'Authorization': this.getAuthHeader(),
        }
      });
      
      if (searchResponse.ok) {
        const existingTags = await searchResponse.json();
        // If we find an exact match, return it
        const exactMatch = existingTags.find(tag => 
          tag.name.toLowerCase() === name.toLowerCase()
        );
        
        if (exactMatch) {
          console.log(`Found existing tag: ${name} (ID: ${exactMatch.id})`);
          return exactMatch;
        }
      }
      
      // If tag doesn't exist, create it
      const createResponse = await fetch(`${this.site.api_url}/tags`, {
        method: 'POST',
        headers: {
          'Authorization': this.getAuthHeader(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: name,
          // You can add additional properties if needed
          // description: '',
          // slug: ''
        })
      });

      if (!createResponse.ok) {
        const errorData = await createResponse.json().catch(() => ({}));
        throw new Error(`Tag creation failed: ${errorData.message || createResponse.statusText}`);
      }

      const newTag = await createResponse.json();
      console.log(`Created new tag: ${name} (ID: ${newTag.id})`);
      return newTag;
    } catch (error) {
      console.error(`Error creating/finding tag "${name}":`, error);
      throw error;
    }
  }

  async getMedia({ 
    page = 1, 
    perPage = 12, // Default to 12 as used in ImageSelectionDialog
    search = '',
    orderBy = 'date',
    order = 'desc',
    context = 'view' // Default to view, can be 'edit'
  } = {}) {
    try {
      let url = `${this.site.api_url}/media?page=${page}&per_page=${perPage}&context=${context}`;
      
      if (search) {
        url += `&search=${encodeURIComponent(search)}`;
      }
      url += `&orderby=${orderBy}&order=${order}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': this.getAuthHeader()
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Failed to fetch media library: ${errorData.message || response.statusText}`);
      }

      const totalPages = parseInt(response.headers.get('X-WP-TotalPages') || '1');
      const totalMedia = parseInt(response.headers.get('X-WP-Total') || '0');
      const media = await response.json();

      return {
        media,
        totalPages,
        totalMedia
      };
    } catch (error) {
      console.error('Error fetching media library:', error);
      throw error;
    }
  }
}

export default WordPressClient;